import React from 'react';
import { DashboardFieldsProvider } from 'domain/shared/core/dashboard';
import { ControlledShow, useShowContext } from 'framework/show';
import { Grid2 as Grid, Typography } from '@mui/material';
import { StaffingDataType } from 'domain/shared/staffing';
import { useStaffingDashboardCallbacks } from '../../dashboard/hooks/useStaffingDashboardCallbacks';
import StaffingDashboardExpandedItemWrapper from '../../dashboard/components/StaffingDashboardExpandedItemWrapper';
import StaffingDashboardModalsProvider from '../../dashboard/components/StaffingDashboardModalsProvider';
import OpportunityDashboardExpandedContent from './OpportunityDashboardExpandedContent';
import OpportunityDashboardGeneralInfo from './OpportunityDashboardGeneralInfo';
import { useAccountUrl } from 'domain/shared/account';
import OpportunityDashboardCollapsedItem from './OpportunityDashboardCollapsedItem';
import { DashboardItem } from 'framework/ui/dashboard';
import { Tooltip } from 'framework/ui/mui-builder';

interface StaffingOpportunityDashboardCardProps {
  project: StaffingDataType;
  onUpdate?: (project: StaffingDataType) => void;
}

const OpportunityDashboardCardContent = () => {
  const { data } = useShowContext<StaffingDataType>();
  const projectData = data.getData();
  const itemId = projectData.alias || projectData.id.toString();
  const { getSingleEntityUrl: getAccountUrl } = useAccountUrl();

  const dashboardCallbacks = useStaffingDashboardCallbacks();

  const collapsedComponent = (
    <OpportunityDashboardCollapsedItem
      accountPath={getAccountUrl(projectData?.accountData?.accountId)}
      crmId={projectData.engagement?.crmId}
      accountName={projectData.accountData?.accountName}
      accountId={projectData.accountData?.accountId}
      assignedFte={projectData.team?.expectedStructure?.reduce(
        (sum, member) => sum + (Number(member.requiredFte) || 0),
        0,
      )}
    />
  );

  const expandedComponent = (
    <StaffingDashboardExpandedItemWrapper bottomContent={<OpportunityDashboardExpandedContent />}>
      <Grid container spacing={1.2} pb={2}>
        <Grid size={{ xs: 3 }}>
          <Tooltip title={'Opportunity ID'}>
            <Typography variant="subtitle1">{projectData.engagement?.crmId}</Typography>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 9 }}>
          <Tooltip title={'Account'}>
            <Typography variant="body1">
              {projectData.accountData?.accountName} - {projectData.accountData?.accountId}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <OpportunityDashboardGeneralInfo />
        </Grid>
      </Grid>
    </StaffingDashboardExpandedItemWrapper>
  );

  return (
    <DashboardFieldsProvider {...dashboardCallbacks}>
      <DashboardItem itemId={itemId} collapsedComponent={collapsedComponent} expandedComponent={expandedComponent} />
    </DashboardFieldsProvider>
  );
};

const OpportunityDashboardCard: React.FC<StaffingOpportunityDashboardCardProps> = ({ project, onUpdate }) => {
  return (
    <ControlledShow data={project} setData={onUpdate}>
      <StaffingDashboardModalsProvider>
        <OpportunityDashboardCardContent />
      </StaffingDashboardModalsProvider>
    </ControlledShow>
  );
};

export default OpportunityDashboardCard;
